import axios from 'axios';
import { readSupportSessionId } from './supportSession';

export const supportSessionHeaders = (): Record<string, string> => ({
  'X-Support-Session-Id': readSupportSessionId(),
});

// Cookies are shared across tabs. A rejected stale request must never be
// retried against the account that replaced it; reload its visible state first.
// An ended-session response also needs document navigation so Rails can clear
// the cookie and show the sign-out notice. A generic 401 must not cause a loop.
export const handleSupportSessionFailure = (
  status: number,
  ended = false,
  reload: () => void = () => window.location.reload(),
): boolean => {
  if (status !== 409 && !(status === 401 && ended)) return false;
  reload();
  return true;
};

export const installSupportSessionRequests = (
  reload: () => void = () => window.location.reload(),
): (() => void) => {
  const requestId = axios.interceptors.request.use((config) => {
    const base = new URL(
      config.baseURL || window.location.href,
      window.location.href,
    );
    const url = new URL(config.url || '', base);
    if (url.origin === window.location.origin) {
      config.headers.set('X-Support-Session-Id', readSupportSessionId());
    }
    return config;
  });
  const responseId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const ended =
        error.response?.headers?.['x-support-session-ended'] === '1';
      if (
        ended ||
        error.response?.data?.error?.code === 'support_session_changed'
      ) {
        handleSupportSessionFailure(error.response.status, ended, reload);
      }
      return Promise.reject(error);
    },
  );
  return () => {
    axios.interceptors.request.eject(requestId);
    axios.interceptors.response.eject(responseId);
  };
};
